import * as React from 'react'
import {
  DEFAULT_LIST_VIEW,
  ListBlock,
  ListView,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { useUserStore } from '~/app/_store/user-store'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useStorage } from '~/hooks/use-storage'

export const useSelectedView = <T extends ListView['layout']>(
  block: ListBlock | undefined | null,
  opts?: { request: T }
): [
  Extract<ListView, { layout: T }> | undefined,
  (newViewId: ListView['id']) => Promise<void>,
] => {
  const { getControls } = useSelectedViewControls()
  const { selectedView, setSelectedView } = getControls<T>(block, opts)
  return [selectedView, setSelectedView]
}

export const useSelectedViewControls = () => {
  const [selectedListsView, setSelectedListView] = useResolvedSelectedView()

  const getControls = React.useCallback(
    <T extends ListView['layout']>(
      block: ListBlock | undefined | null,
      opts?: { request: T }
    ): {
      selectedView: Extract<ListView, { layout: T }> | undefined
      setSelectedView: (newViewId: ListView['id']) => Promise<void>
    } => {
      if (!block) {
        return { selectedView: undefined, setSelectedView: async () => {} }
      }

      const selectedViewId =
        selectedListsView[idToSourceBlockId(block)] ??
        block.value.views?.[0]?.id
      const selectedView =
        block.value.views?.find((view) => view.id === selectedViewId) ??
        block.value.views?.[0] ??
        DEFAULT_LIST_VIEW

      if (selectedView && opts?.request) {
        invariant(
          selectedView.layout === opts.request,
          `Requested selected view for list ${idToSourceBlockId(block)} to be ${
            opts.request
          }, found ${selectedView.layout}`
        )
      }

      const setSelectedView = async (newViewId: ListView['id']) => {
        await setSelectedListView(newViewId, block)
      }

      return {
        selectedView: selectedView as
          | undefined
          | Extract<ListView, { layout: T }>,
        setSelectedView,
      }
    },
    [selectedListsView, setSelectedListView]
  )

  return { getControls, selectedListsView }
}

const useResolvedSelectedView = (): [
  Record<string, string>,
  (newViewId: string, block: ListBlock) => void,
] => {
  let selectedListViews
  let setSelectedListView

  const userId = useUserStore((state) => state.optimisticUser?.id)
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const isLoggedIn = useUserStore((s) => s.optimisticUser?.id)
  const selectedListViewsUserPreferences = useUserPreferences(
    (s) => s.listSelectedView
  )
  const [selectedListViewsStorage, setSelectedListViewsStorage] = useStorage(
    'sessionStorage',
    (z) => z.record(z.string(), z.string()),
    'selected-list-views',
    { enabled: !isLoggedIn, initialValue: {} }
  )

  if (isLoggedIn) {
    selectedListViews = selectedListViewsUserPreferences
    setSelectedListView = async (newViewId: string, block: ListBlock) => {
      if (!replicache || !userId) return

      await replicache.mutate.setSelectedListView({
        userId,
        listBlockId: idToSourceBlockId(block),
        listBlockViewId: newViewId,
      })
    }
  } else {
    selectedListViews = selectedListViewsStorage
    setSelectedListView = async (newViewId: string, block: ListBlock) => {
      setSelectedListViewsStorage((prev) => ({
        ...prev,
        [idToSourceBlockId(block)]: newViewId,
      }))
    }
  }

  return [selectedListViews, setSelectedListView]
}
